import React, { useState, useEffect } from 'react';
import { SbModal, SbForm, SbInput, SbButton, SbNotification, SbImageCropUpload } from '../../../../uiCore';
import { UploadZone } from '../../AdminSetup.styles';

const BrandingModal = ({ visible, onClose, initialData, onSave, cardKey }) => {
  const [form] = SbForm.useForm();
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (visible && initialData) {
      form.setFieldsValue({
        brand_name: initialData.org_name || '',
        website: initialData.org_url || '',
      });
      setLogoPreview(initialData.org_logo_url || null);
      setLogoFile(null);
    }
  }, [visible, initialData, form]);

  const handleLogoChange = (file) => {
    setLogoFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const changedFields = {};

      if (values.brand_name !== initialData?.org_name) {
        changedFields.org_name = values.brand_name;
      }

      if (values.website !== initialData?.org_url) {
        changedFields.org_url = values.website;
      }
      
      if (logoFile) {
        changedFields.org_logo_url = await convertToBase64(logoFile);
      }

      if (Object.keys(changedFields).length === 0) {
        SbNotification.info('No Changes', 'No changes detected');
        onClose();
        return;
      }

      const result = await onSave(cardKey, changedFields);
      if (result.success) {
        onClose();
        setLogoFile(null);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setLogoFile(null);
    setLogoPreview(initialData?.org_logo_url || null);
    onClose();
  };

  const renderUploadArea = (previewUrl) => (
    <UploadZone>
      {previewUrl ? (
        <div>
          <img 
            src={previewUrl} 
            alt="Logo preview" 
            style={{ maxHeight: '80px', maxWidth: '80px', marginBottom: '12px' }} 
          />
          <p>Click to change logo</p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
          <p>Upload company logo</p>
          <span>PNG, JPG, SVG · max 2MB · 1:1 aspect ratio</span>
        </>
      )}
    </UploadZone>
  );

  return (
    <SbModal
      title="Branding"
      open={visible}
      onCancel={handleCancel}
      width={480}
      footer={[
        <SbButton key="cancel" buttonType="secondary" onClick={handleCancel}>
          Cancel
        </SbButton>,
        <SbButton key="save" buttonType="primary" loading={loading} onClick={handleSave}>
          Save
        </SbButton>
      ]}
    >
      <SbForm form={form} layout="vertical">
        <SbForm.Item
          label="Company Name"
          name="brand_name"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <SbInput placeholder="e.g. Acme Corporation" />
        </SbForm.Item>

        <SbForm.Item
          label="Company Website"
          name="website"
          rules={[
            { required: true, message: 'Please enter website URL' },
            { type: 'url', message: 'Please enter a valid URL (must include http:// or https://)' }
          ]}
        >
          <SbInput placeholder="https://example.com" />
        </SbForm.Item>

        <SbForm.Item label="Company Logo">
          <SbImageCropUpload
            value={logoPreview}
            onChange={handleLogoChange}
            accept=".png,.jpg,.jpeg,.svg"
            maxSize={2}
            aspectRatio={1}
            uploadText="Upload company logo"
            uploadHint="PNG, JPG, SVG · max 2MB · 1:1 aspect ratio"
            previewSize={{ maxHeight: '80px', maxWidth: '80px' }}
            renderUploadArea={renderUploadArea}
          />
        </SbForm.Item>
      </SbForm>
    </SbModal>
  );
};

export default BrandingModal;
